import { createReducer, on } from '@ngrx/store';
import { IEstensaButtonStatus, IEstensaButtonStatusWithChildren } from '@se/se.ui.advanced';
import { keyBy, cloneDeep, mapValues } from 'lodash-es';

import * as actions from './se-buttons-toolbar.actions';
import { seButtonsToolbarInitialState, SeButtonsToolbarState } from './se-buttons-toolbar.state';
import { ToolbarAction, ToolbarButtonData, ToolbarSliderOptions, ToolbarTimelineOptions } from '../types';

export const seButtonsToolbarReducer = createReducer(
    seButtonsToolbarInitialState,
    on(actions.seToolbarButtonEnable, (state, action) => enableButton(state, action.button, action.enabled)),
    on(actions.seToolbarButtonHide, (state, action) => hideButton(state, action.button, action.hidden)),
    on(actions.seToolbarButtonToggle, (state, action) => toggleButton(state, action.button, action.checked)),
    on(actions.seToolbarInitialize, (state, action) =>
        initialize(state, action.buttons, action.leftSlider, action.rightSlider, action.timeline)
    ),
    on(actions.seToolbarInitializeButtons, (state, action) => initializeButtons(state, action.buttons)),
    on(actions.seToolbarSetAllButtons, (state, action) => setAllButtons(state, action.buttons)),
    on(actions.seToolbarSetButton, (state, action) => setButton(state, action.action, action.button)),
    on(actions.seToolbarSetLeftSlider, (state, action) => ({ ...state, leftSlider: action.status })),
    on(actions.seToolbarSetTimeline, (state, action) => ({ ...state, timeline: action.status })),
    on(actions.seToolbarSetRightSlider, (state, action) => ({ ...state, rightSlider: action.status })),
    on(actions.seToolbarToggleLeftSlider, (state) => toggleLeftSlider(state)),
    on(actions.seToolbarToggleRightSlider, (state) => toggleRightSlider(state)),
    on(actions.seToolbarToggleTimeline, (state) => toggleTimeline(state)),
    on(actions.seToolbarSetButtonChildren, (state, action) => setButtonChildren(state, action.button, action.children)),
    on(actions.seToolbarDisableAllButtons, (state) => disableAllButtons(state))
);

function initializeButtons(state: SeButtonsToolbarState, buttonArr: ToolbarButtonData[]): SeButtonsToolbarState {
    const buttons = keyBy(buttonArr, 'actionType');
    return {
        ...state,
        buttons,
    };
}

function initialize(
    state: SeButtonsToolbarState,
    buttonArr: ToolbarButtonData[],
    leftSlider: ToolbarSliderOptions,
    rightSlider : ToolbarSliderOptions,
    timeline: ToolbarTimelineOptions,
): SeButtonsToolbarState {
    const buttons = keyBy(buttonArr, 'actionType');
    return {
        ...state,
        buttons,
        leftSlider,
        rightSlider,
        timeline
    };
}

function setAllButtons(
    state: SeButtonsToolbarState,
    newButtonState: Partial<Record<ToolbarAction, IEstensaButtonStatus | IEstensaButtonStatusWithChildren>>
): SeButtonsToolbarState {
    const buttons = cloneDeep(state.buttons);

    for (const key in newButtonState) {
        const action = key as ToolbarAction;
        if (Object.hasOwnProperty.call(newButtonState, action) && buttons[action]) {
            const newStatus = newButtonState[action];
            const button = buttons[action];
            let activeChildren = 0;
            if ('children' in newStatus) {
                button.children?.forEach(
                    (child) => {
                        child.status = newStatus.children[child.actionType] ?? child.status;
                        activeChildren = (!child.status.disabled) ? activeChildren + 1 : activeChildren;
                    }
                );
                if (activeChildren === 0) {
                    button.status = { visible: newStatus.status.visible, disabled: true, checked: newStatus.status.checked };
                }
                else {
                    button.status = newStatus.status;
                }
            } else {
                button.status = newStatus;
            }
        }
    }

    return {
        ...state,
        buttons,
    };
}

function setButton(
    state: SeButtonsToolbarState,
    action: ToolbarAction,
    status: Partial<ToolbarButtonData>
): SeButtonsToolbarState {
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: {
                ...state.buttons[action],
                ...status,
            },
        },
    };
}

function disableAllButtons(state: SeButtonsToolbarState): SeButtonsToolbarState {
    const buttons = mapValues(state.buttons, (button) => {
        return { ...button, status: { ...button.status, disabled: true } };
    });

    return {
        ...state,
        buttons,
    };
}

function enableButton(state: SeButtonsToolbarState, action: ToolbarAction, enabled: boolean) {
    const oldButton = state.buttons[action];
    if (!oldButton || oldButton.status.disabled === !enabled) {
        return state;
    }
    const newButton = cloneDeep(oldButton);
    newButton.status.disabled = !enabled;
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: newButton,
        },
    };
}

function hideButton(state: SeButtonsToolbarState, action: ToolbarAction, hidden: boolean) {
    const oldButton = state.buttons[action];
    if (!oldButton || oldButton.status.visible === !hidden) {
        return state;
    }
    const newButton = cloneDeep(oldButton);
    newButton.status.visible = !hidden;
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: newButton,
        },
    };
}

function toggleButton(
    state: SeButtonsToolbarState,
    action: ToolbarAction,
    checked: boolean
): SeButtonsToolbarState {
    const oldButton = state.buttons[action];
    if (!oldButton || oldButton.status.checked === checked) {
        return state;
    }
    const newButton = cloneDeep(oldButton);
    newButton.status.checked = checked;
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: newButton,
        },
    };
}

function toggleLeftSlider(state: SeButtonsToolbarState): SeButtonsToolbarState {
    return {
        ...state,
        leftSlider: {
            ...state.leftSlider,
            checked: !state.leftSlider.checked,
        },
    };
}

function toggleRightSlider(state: SeButtonsToolbarState): SeButtonsToolbarState {
    return {
        ...state,
        rightSlider: {
            ...state.rightSlider,
            checked: !state.rightSlider.checked,
        },
    };
}

function setButtonChildren(state: SeButtonsToolbarState, action: ToolbarAction, children: ToolbarButtonData[]) {
    if (children && children.length > 0 && state.buttons[action]) {
        const oldButton = state.buttons[action];
        const newButton = cloneDeep(oldButton);
        newButton.children = children;
        newButton.hasChildren = true;
        return {
            ...state,
            buttons: {
                ...state.buttons,
                [action]: newButton,
            },
        };
    } else {
        return state;
    }
}

function toggleTimeline (state : SeButtonsToolbarState) : SeButtonsToolbarState {
    return {
        ...state,
        timeline: {
            ...state.timeline,
            open: !state.timeline.open,
        },
    };
}
